import { useEffect } from "react";
import { useAuthenticationStore } from "../stores/auth";
import { jwtDecode } from "jwt-decode";
import { useApiClient } from "../api";

const getExpiryTime = (token) => jwtDecode(token).exp * 1000;

export const useTokenRefresh = () => {
  const { user, setUser, logOutUser, updateUserInfo } =
    useAuthenticationStore();
  const apiClient = useApiClient();

  useEffect(() => {
    if (!user?.token || !user?.refreshToken || user.beingImpersonated) {
      return;
    }

    const checkAndRefresh = () => {
      const expiryTime = getExpiryTime(user.token);
      if (expiryTime - Date.now() <= 60000) {
        refreshToken();
      }
    };

    const interval = setInterval(checkAndRefresh, 30000);
    return () => clearInterval(interval);
  }, [user?.token, user?.refreshToken, user?.beingImpersonated]);

  const refreshToken = async () => {
    try {
      const newTokenData = await apiClient.users.refreshToken(
        user.refreshToken,
      );
      if (!newTokenData?.token) {
        logOutUser();
        return;
      }
      setUser({
        preserveSession: true,
        token: newTokenData.token,
        refreshToken: newTokenData.refreshToken,
        refreshExpiresIn: jwtDecode(newTokenData.token).exp * 1000,
      });
      try {
        const userData = await apiClient.users.me();
        updateUserInfo(userData);
      } catch (error) {
        console.error("Failed to fetch user details:", error);
      }
    } catch (error) {
      console.error("Token refresh failed:", error);
      logOutUser();
    }
  };
};

export const useImpersonatorTokenRefresh = () => {
  const { user, impersonation, setUser } = useAuthenticationStore();
  const ApiClient = useApiClient();

  useEffect(() => {
    if (!impersonation.isActive) return;

    const checkAndRefresh = () => {
      let needsRefresh = false;

      if (user?.token) {
        const expiryTime = getExpiryTime(user.token);
        if (expiryTime - Date.now() <= 60000) {
          needsRefresh = true;
        }
      }

      if (impersonation.originalSession?.token) {
        const expiryTime = getExpiryTime(impersonation.originalSession.token);
        if (expiryTime - Date.now() <= 60000) {
          needsRefresh = true;
        }
      }

      if (needsRefresh) {
        refreshBoth();
      }
    };

    const interval = setInterval(checkAndRefresh, 30000);
    return () => clearInterval(interval);
  }, [
    impersonation.isActive,
    user.token,
    impersonation.originalSession?.token,
  ]);

  const refreshBoth = async () => {
    try {
      const newImpersonated = await ApiClient.users.refreshToken(
        user.refreshToken,
      );
      if (newImpersonated?.token) {
        setUser({
          preserveSession: true,
          token: newImpersonated.token,
          refreshToken: newImpersonated.refreshToken,
          refreshExpiresIn: getExpiryTime(newImpersonated.token),
        });
      }

      if (impersonation.originalSession?.refreshToken) {
        const newOriginal = await ApiClient.users.refreshToken(
          impersonation.originalSession.refreshToken,
        );
        if (newOriginal?.token) {
          useAuthenticationStore.setState((state) => ({
            impersonation: {
              ...state.impersonation,
              originalSession: {
                ...state.impersonation.originalSession,
                token: newOriginal.token,
                refreshToken: newOriginal.refreshToken,
                refreshExpiresIn: getExpiryTime(newOriginal.token),
              },
            },
          }));
        }
      }

      await ApiClient.users.client.clearStore();
    } catch (err) {
      console.error("Failed to refresh impersonation tokens:", err);
    }
  };
};
